// Before you fly (F500, decision 110): the page a pilot meets once after
// the first sign-in, before anything else: the product is in beta, the
// first and last name are needed, the privacy choices are confirmed, the
// privacy notice is read. The platform keeps the moment (`welcomed_at`);
// the root sends a pilot here while it is null.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { api, queries, unwrap, type Me } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { SignInNeeded } from "@/components/SignInNeeded";
import { useHost } from "@/host/host";

export const Route = createFileRoute("/welcome")({
  component: Welcome,
});

function Welcome() {
  const me = useQuery(queries.me());
  const navigate = useNavigate();
  const welcomed = !!me.data?.user.welcomed_at;
  // Already through it: nothing to see here.
  useEffect(() => {
    if (welcomed) navigate({ to: "/" });
  }, [welcomed, navigate]);
  if (me.isPending) return null;
  if (!me.data) return <SignInNeeded what="the way in" />;
  if (welcomed) return null;
  return <BeforeYouFly key={me.data.user.id} me={me.data} />;
}

function BeforeYouFly({ me }: { me: Me }) {
  const host = useHost();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const prefs = useQuery(queries.preferences());
  const [firstName, setFirstName] = useState(me.user.first_name ?? "");
  const [lastName, setLastName] = useState(me.user.last_name ?? "");
  const [nameShown, setNameShown] = useState<"full" | "handle" | null>(null);
  const [seenBy, setSeenBy] = useState<"members" | "managers" | null>(null);
  const [pictureShown, setPictureShown] = useState<boolean | null>(null);
  const [read, setRead] = useState(false);
  const shown = nameShown ?? (prefs.data?.name_shown === "handle" ? "handle" : "full");
  const seen = seenBy ?? (prefs.data?.seen_by === "managers" ? "managers" : "members");
  const picture = pictureShown ?? prefs.data?.picture_shown ?? true;
  const ready = firstName.trim().length > 0 && lastName.trim().length > 0 && read && !!prefs.data;
  const go = useMutation({
    mutationFn: async () => {
      // The choices first (they are kept whatever the names say), then the
      // names with the welcome, which the platform refuses without both.
      if (prefs.data && (shown !== prefs.data.name_shown || seen !== prefs.data.seen_by || picture !== prefs.data.picture_shown)) {
        unwrap(await api.PATCH("/api/v1/me/preferences", { body: { name_shown: shown, seen_by: seen, picture_shown: picture } }));
      }
      return unwrap(await api.PATCH("/api/v1/me", { body: { first_name: firstName.trim(), last_name: lastName.trim(), welcomed: true } }));
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(queries.me().queryKey, fresh);
      queryClient.invalidateQueries({ queryKey: ["preferences"] });
      // Under the companion the set-up comes next (the plugin, the folder,
      // the first aircraft); in the browser, the fleet.
      navigate({ to: host ? "/setup" : "/" });
    },
  });
  return (
    <div className="mx-auto max-w-2xl space-y-6 pt-4" data-testid="before-you-fly">
      <header className="space-y-1">
        <h1 className="page-title">Before you fly</h1>
        <p className="muted text-sm">Three things, once. Then the aircraft.</p>
      </header>
      <section className="notice space-y-2" data-testid="beta-notice">
        <h2 className="font-semibold">ShariFly is in beta</h2>
        <p className="text-sm">
          The platform, the app on your PC and the plugin are young and change often. Bugs may appear: a return refused, a figure wrong, a page that does not load. Nothing of your simulator&apos;s
          own files is ever changed, and every aircraft you book is backed up on your PC before it is installed; what can go wrong is the record, and the administrators can restore an earlier entry.
          When something looks wrong, say so from Help, with what you did and when: that is how the beta gets better.
        </p>
      </section>
      <form
        className="card space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) go.mutate();
        }}
      >
        <div className="space-y-3">
          <div>
            <h2 className="font-semibold">Your name</h2>
            <p className="muted text-xs">The other pilots fly a real aircraft with you; they need to know who you are. Your handle, @{me.user.handle}, stays the name you sign in with.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="welcome-first-name">
                First name
              </label>
              <input id="welcome-first-name" className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={60} autoComplete="given-name" required data-testid="welcome-first-name" />
            </div>
            <div>
              <label className="label" htmlFor="welcome-last-name">
                Last name
              </label>
              <input id="welcome-last-name" className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={60} autoComplete="family-name" required data-testid="welcome-last-name" />
            </div>
          </div>
        </div>
        <div className="space-y-3 border-t border-line pt-4" data-testid="welcome-privacy">
          <div>
            <h2 className="font-semibold">What the other pilots see of you</h2>
            <p className="muted text-xs">Your choice, kept on the platform and applied wherever you appear; you can change it any time under Account & privacy.</p>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-[0.9286rem] font-semibold">Your name</legend>
            <label className="flex items-start gap-2 text-[0.9286rem]">
              <input type="radio" name="welcome-name-shown" className="mt-1" checked={shown === "full"} onChange={() => setNameShown("full")} data-testid="welcome-name-full" />
              <span>The name above</span>
            </label>
            <label className="flex items-start gap-2 text-[0.9286rem]">
              <input type="radio" name="welcome-name-shown" className="mt-1" checked={shown === "handle"} onChange={() => setNameShown("handle")} data-testid="welcome-name-handle" />
              <span>Your handle alone, @{me.user.handle}</span>
            </label>
          </fieldset>
          <label className="flex items-center gap-2 text-[0.9286rem]">
            <input type="checkbox" checked={picture} onChange={(e) => setPictureShown(e.target.checked)} data-testid="welcome-picture" />
            <span>Show my Discord picture beside my name</span>
          </label>
          <fieldset className="space-y-2">
            <legend className="text-[0.9286rem] font-semibold">Who sees you</legend>
            <label className="flex items-start gap-2 text-[0.9286rem]">
              <input type="radio" name="welcome-seen-by" className="mt-1" checked={seen === "members"} onChange={() => setSeenBy("members")} data-testid="welcome-seen-members" />
              <span>
                Every member of the aircraft you share
                <span className="muted block text-xs">They see who has the aircraft, who flew it, who wrote in the tech log.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-[0.9286rem]">
              <input type="radio" name="welcome-seen-by" className="mt-1" checked={seen === "managers"} onChange={() => setSeenBy("managers")} data-testid="welcome-seen-managers" />
              <span>
                The owner and the captains only
                <span className="muted block text-xs">To the other members you are &quot;a pilot&quot;, with no name and no picture. An aircraft you own always names you as its owner.</span>
              </span>
            </label>
          </fieldset>
        </div>
        <div className="space-y-3 border-t border-line pt-4">
          <div>
            <h2 className="font-semibold">Your data</h2>
            <p className="muted text-xs">
              The platform keeps your profile, your memberships, your bookings, the flights and the events your aircraft reports, and the aircraft&apos;s own state files, which hold the FMS
              flight log and the ACARS messages. The members of the aircraft you share see them under the choices above; you can delete the account yourself and ask for an export.{" "}
              <Link to="/privacy" className="underline" data-testid="welcome-privacy-link">
                Read how your data is kept
              </Link>
              .
            </p>
          </div>
          <label className="flex items-start gap-2 text-[0.9286rem]">
            <input type="checkbox" className="mt-1" checked={read} onChange={(e) => setRead(e.target.checked)} data-testid="welcome-read" />
            <span>I read how my data is kept and I know the platform is in beta.</span>
          </label>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" className="btn btn-primary" disabled={!ready || go.isPending} data-testid="welcome-continue">
            Continue
          </button>
          <span className="muted text-xs">{host ? "Next: the app's set-up on this PC." : "Next: your fleet."}</span>
        </div>
        <ErrorNote error={go.error} />
      </form>
    </div>
  );
}
